// Perfil › Mi cuenta (decisión D51): el correo con que entra y las cuentas de Google, Apple o Facebook vinculadas.
// Vincular usa la misma ida y vuelta que entrar, con la sesión abierta: la API vuelve a `/perfil#c=…&p=…&t=vincular`
// y acá se termina con `POST /auth/vincular`. Facebook solo entra si se vinculó acá (no se une por el correo).
import { mensajeGooglePersonal, NOMBRE_PROVEEDOR, PROVEEDORES, type Proveedor } from '@reparto/shared/nucleo';
import { Boton, HojaInferior, Icono, LogoProveedor, Tarjeta, useToast } from '@reparto/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ErrorApi, SinRed } from '../datos/api';
import {
  type CuentaPersonal,
  desvincular,
  irAVincular,
  miCuenta,
  opcionesAcceso,
  proveedoresDe,
  terminarVinculo,
  tomarSecreto,
  tomarVuelta,
} from '../datos/google';
import { AvisoError } from './MarcoAcceso';

/** Por qué no se pudo vincular o quitar, en palabras del chofer. */
function motivo(e: unknown, p: Proveedor): string {
  if (e instanceof SinRed) return 'No hay conexión. Probá de nuevo cuando tengas señal.';
  if (e instanceof ErrorApi && e.codigo === 'DUPLICADO') return mensajeGooglePersonal('ya_vinculada', 'chofer', p);
  if (e instanceof ErrorApi && e.codigo === 'GOOGLE_VENCIDO')
    return `Se venció o se abrió en otro navegador. Tocá «Vincular» de nuevo.`;
  if (e instanceof ErrorApi) return e.message;
  return `No se pudo vincular ${NOMBRE_PROVEEDOR[p]}. Probá de nuevo.`;
}

export function SeccionCuenta({ correo }: { correo: string | null }) {
  const { mostrar } = useToast();
  const [cuenta, setCuenta] = useState<CuentaPersonal | null>(null);
  const [sinRed, setSinRed] = useState(false);
  const [disponibles, setDisponibles] = useState<Proveedor[]>([]);
  const [error, setError] = useState<string | null>(null);
  /** Yendo al proveedor (ida) o terminando de vincular con lo que trajo la vuelta. */
  const [ocupado, setOcupado] = useState<{ p: Proveedor; paso: 'ida' | 'vuelta' } | null>(null);
  const [quitar, setQuitar] = useState<Proveedor | null>(null);
  const [quitando, setQuitando] = useState(false);
  // Una sola vez aunque React monte dos veces en desarrollo: el código y el secreto se gastan al usarlos.
  const vueltaLeida = useRef(false);

  const cargar = useCallback(async () => {
    try {
      setCuenta(await miCuenta());
      setSinRed(false);
    } catch {
      setSinRed(true);
    }
  }, []);

  useEffect(() => {
    let vivo = true;
    void opcionesAcceso().then((o) => vivo && setDisponibles(proveedoresDe(o)));
    const alMostrar = (e: PageTransitionEvent) => e.persisted && setOcupado(null);
    window.addEventListener('pageshow', alMostrar);
    return () => {
      vivo = false;
      window.removeEventListener('pageshow', alMostrar);
    };
  }, []);

  useEffect(() => {
    if (vueltaLeida.current) return;
    vueltaLeida.current = true;
    const v = tomarVuelta();
    if (!v?.vincular) {
      void cargar();
      return;
    }
    const p = v.proveedor;
    // Se vuelve del proveedor: que se vea el resultado sin buscarlo.
    setTimeout(() => document.getElementById('cuenta')?.scrollIntoView({ block: 'start' }), 0);
    const secreto = v.codigo ? tomarSecreto() : null;
    if (v.error || !v.codigo || !secreto) {
      setError(mensajeGooglePersonal(v.error ?? 'vencido', 'chofer', p));
      void cargar();
      return;
    }
    setOcupado({ p, paso: 'vuelta' });
    terminarVinculo(v.codigo, secreto)
      .then((r) => mostrar(`Listo: ya podés entrar con ${NOMBRE_PROVEEDOR[r.proveedor]}`, { tipo: 'ok' }))
      .catch((e: unknown) => setError(motivo(e, p)))
      .finally(() => {
        setOcupado(null);
        void cargar();
      });
  }, [cargar, mostrar]);

  function vincular(p: Proveedor) {
    setError(null);
    setOcupado({ p, paso: 'ida' });
    irAVincular(p).catch((e: unknown) => {
      setOcupado(null);
      setError(motivo(e, p));
    });
  }

  async function confirmarQuitar() {
    if (!quitar) return;
    const p = quitar;
    setQuitando(true);
    try {
      await desvincular(p);
      mostrar(`Se quitó ${NOMBRE_PROVEEDOR[p]}`);
      setQuitar(null);
      await cargar();
    } catch (e) {
      setQuitar(null);
      setError(motivo(e, p));
    } finally {
      setQuitando(false);
    }
  }

  const vinculados = cuenta?.proveedores ?? [];
  // Los que el servidor tiene y los que ya están vinculados (aunque hoy no estén), en el orden de siempre.
  const filas = PROVEEDORES.filter((p) => disponibles.includes(p) || vinculados.some((v) => v.proveedor === p));
  const email = cuenta?.email ?? correo;

  return (
    <Tarjeta className="scroll-mt-4 space-y-3" id="cuenta">
      <h2 className="font-medium">Mi cuenta</h2>
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300">
          <Icono nombre="mail" />
        </span>
        <div className="min-w-0">
          <div className="text-xs text-stone-500 dark:text-stone-400">Entrás con</div>
          <div className="font-medium break-all">{email ?? 'Sin correo cargado'}</div>
        </div>
      </div>
      {!email ? (
        <p className="text-sm text-stone-600 dark:text-stone-300">
          Pedile al administrador que te cargue un correo para entrar con contraseña.
        </p>
      ) : cuenta && !cuenta.tiene_clave ? (
        <p className="text-sm text-stone-600 dark:text-stone-300">
          Todavía no tenés contraseña: pedile al administrador el enlace para crearla.
        </p>
      ) : null}
      {error ? <AvisoError>{error}</AvisoError> : null}
      {filas.length ? (
        <>
          <ul className="divide-y divide-stone-100 rounded-2xl border border-stone-200 dark:divide-stone-800 dark:border-stone-800">
            {filas.map((p) => {
              const v = vinculados.find((x) => x.proveedor === p);
              const aca = ocupado?.p === p;
              return (
                <li key={p} className="flex items-center gap-3 px-3 py-2.5">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-stone-200 bg-white text-stone-900 dark:border-stone-700 dark:bg-stone-800 dark:text-white">
                    <LogoProveedor proveedor={p} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{NOMBRE_PROVEEDOR[p]}</div>
                    <div className="truncate text-xs text-stone-500 dark:text-stone-400">
                      {aca && ocupado.paso === 'vuelta'
                        ? 'Vinculando…'
                        : v
                          ? `Vinculada${v.email ? ` · ${v.email}` : ''}`
                          : 'Sin vincular'}
                    </div>
                  </div>
                  {v ? (
                    <Boton variante="secundario" className="px-4" disabled={!!ocupado} onClick={() => setQuitar(p)}>
                      Quitar
                    </Boton>
                  ) : (
                    <Boton
                      variante="secundario"
                      className="px-4"
                      icono="link"
                      cargando={aca}
                      disabled={sinRed || !!ocupado || !disponibles.includes(p)}
                      onClick={() => vincular(p)}
                    >
                      Vincular
                    </Boton>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="text-xs text-stone-500 dark:text-stone-400">
            Con una cuenta vinculada entrás con un toque, sin escribir la contraseña. Facebook solo funciona si la
            vinculás acá.
          </p>
        </>
      ) : null}
      {sinRed ? (
        <p className="flex items-center gap-1.5 text-xs text-stone-500 dark:text-stone-400">
          <Icono nombre="cloud_off" tamano={16} />
          Sin señal: las cuentas vinculadas se ven cuando vuelva.
        </p>
      ) : null}

      <HojaInferior
        abierta={!!quitar}
        onCerrar={() => setQuitar(null)}
        titulo={quitar ? `¿Quitar ${NOMBRE_PROVEEDOR[quitar]}?` : ''}
      >
        <p className="mb-4 text-sm text-stone-600 dark:text-stone-300">
          {quitar ? `Ya no vas a poder entrar con ${NOMBRE_PROVEEDOR[quitar]}. ` : ''}
          {cuenta?.tiene_clave
            ? 'Seguís entrando con tu correo y tu contraseña.'
            : 'Ojo: todavía no tenés contraseña. Pedile al administrador el enlace para crearla.'}
        </p>
        <Boton
          bloque
          tamano="lg"
          variante="peligro"
          icono="link_off"
          cargando={quitando}
          onClick={() => void confirmarQuitar()}
        >
          Sí, quitar
        </Boton>
      </HojaInferior>
    </Tarjeta>
  );
}
